import { S25Util } from "../../../../util/s25-util";
import { S25Number } from "../../../../util/core/s25.number";
import { S25String } from "../../../../util/core/s25.string";
import { Report } from "../../../../pojo/Report";
import { EmailRole } from "../../../../pojo/EventEmailDataI";
import { FormAssignmentRecipient } from "../../../data-ingestion/data.ingestion.service";
import { DocumentListItem } from "../../../../services/document.service";

export namespace PaymentEmailTemplate {
    export const ALL_PROFILES_ID = -999;

    export type EventProfile = { profile_id: number; profile_name: string };

    /** BPE reads "every segment" as a null profile, where the form uses a sentinel id. */
    export function templateProfileId(profileId: number): number {
        return profileId === ALL_PROFILES_ID ? (null as unknown as number) : profileId;
    }

    /** What a BPE manual template evaluates to. Every field is optional - a template may set only a body. */
    export type TemplateEmail = {
        to?: string;
        cc?: string;
        bcc?: string;
        from?: string;
        subject?: string;
        body?: string;
        reports?: string;
    };

    /**
     * Every event role that has a contact behind it, keyed on the role name a template would write.
     * A template addresses roles by name ("Scheduler", "Requestor"), so this is what turns those
     * names into the recipient chips the payment form shows.
     */
    export function roleRecipientsByName(eventData: { role?: EmailRole | EmailRole[] } | undefined) {
        const byName = new Map<string, FormAssignmentRecipient>();

        for (const role of S25Util.array.forceArray(eventData?.role) as EmailRole[]) {
            const contact = role?.contact;
            if (!role?.role_name || !contact?.email) continue;

            const contactId = contact.contact_id ?? contact.contactId;
            byName.set(role.role_name.trim().toLowerCase(), {
                contId: contactId ? Number(contactId) : undefined,
                email: contact.email,
                displayName: contact.contact_name ?? role.role_name,
            });
        }

        return byName;
    }

    /**
     * A template's recipient field is a mix of role names and literal addresses. Roles resolve through
     * the event; anything else is kept only when it looks like an address, so pseudo-recipients the
     * payment form has no equivalent for ("assigners", "object owner") are dropped rather than mailed.
     */
    export function templateRecipients(
        field: string | undefined,
        roleRecipients: ReadonlyMap<string, FormAssignmentRecipient>,
    ): FormAssignmentRecipient[] {
        const byEmail = new Map<string, FormAssignmentRecipient>();

        for (const token of (field ?? "").split(/[,;]/).map(S25Util.trim).filter(S25Util.isTruthy)) {
            const role = roleRecipients.get(token.toLowerCase());
            const recipient = role ?? (S25Util.isValidEmail(token) ? { email: token } : undefined);
            if (recipient?.email) byEmail.set(recipient.email.toLowerCase(), recipient);
        }

        return [...byEmail.values()];
    }

    export function attachableReports(reports: Report.SimpleObject[]): Report.SimpleObject[] {
        return reports.filter((report) => !Report.ShellReportIds.has(Number(report.rpt_id)));
    }

    /**
     * The documents built on the event and organization shells. A template names one of these by
     * its report id, and only a template may attach one - they are not offered otherwise.
     */
    export function documentReports(
        reports: Report.SimpleObject[],
        eventDocuments: DocumentListItem[],
        organizationDocuments: DocumentListItem[],
    ): Report.SimpleObject[] {
        const regularReportIds = new Set(attachableReports(reports).map((report) => Number(report.rpt_id)));
        const findShell = (shellId: number) => reports.find((report) => Number(report.rpt_id) === shellId);
        const byId = new Map<number, Report.SimpleObject>();

        for (const document of [
            ...Report.shellDocumentReports(findShell(Report.Reports.EventShell.id), eventDocuments),
            ...Report.shellDocumentReports(findShell(Report.Reports.OrganizationShell.id), organizationDocuments),
        ]) {
            const reportId = Number(document.rpt_id);
            if (regularReportIds.has(reportId) || byId.has(reportId)) continue;
            byId.set(reportId, { ...document, rpt_id: reportId });
        }

        return [...byId.values()];
    }

    /**
     * The report ids a template names that none of the given reports answer to - candidates for a
     * document. The `confirmation` / `invoice` keywords are never among them.
     */
    export function unmatchedReportIds(field: string | undefined, reports: Report.SimpleObject[]): number[] {
        const known = new Set(reports.map((report) => Number(report.rpt_id)));
        const ids = (field ?? "")
            .split(",")
            .map((token) => S25Number.parseInt(S25String.trim(token)))
            .filter((reportId) => Number.isFinite(reportId) && !known.has(reportId));
        return [...new Set(ids)];
    }

    /**
     * Which reports a template asks to attach. Tokens are report ids or the `confirmation` / `invoice`
     * keywords, matching how `s25-ng-email-event` reads the same field.
     */
    export function templateReportIds(field: string | undefined, reports: Report.SimpleObject[]): number[] {
        const tokens = (field ?? "").split(",").map(S25Util.trim).filter(S25Util.isTruthy);
        if (!tokens.length) return [];

        const ids = new Set<number>();
        for (const token of tokens) {
            const tokenId = parseInt(token);
            for (const report of reports) {
                const matches =
                    (token === "confirmation" && Number(report.rpt_use) === Report.Use.Confirmation) ||
                    (token === "invoice" && Number(report.rpt_use) === Report.Use.Invoice) ||
                    (!isNaN(tokenId) && tokenId === Number(report.rpt_id));
                if (matches) ids.add(Number(report.rpt_id));
            }
        }

        return [...ids];
    }
}
